import { debounce } from '@mui/material/utils';
import { createColumnHelper } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '../../../../trpc/react';
import { Gallery } from '../../../components/gallery/gallery';
import { useViewer } from '../../../context/viewer';
import { useFilteredGalleryPagination } from '../../../hooks/useFilteredGalleryPagination';
import { useToast } from '../../../hooks/useToast';
import {
  NameEntity,
  NameEntityDrawer,
  NameEntityForm,
} from '../components/NameEntityDrawer';

const columnHelper = createColumnHelper<NameEntity>();

export function ExerciseTypesPage() {
  const { t } = useTranslation();
  const { hasPermissions } = useViewer();
  const enableCreate = hasPermissions('library.tags', 'create');
  const { toast } = useToast();
  const [searchInput, setSearchInput] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');

  const {
    refetch,
    data,
    pageSize,
    setPageSize,
    page,
    setPage,
    viewMode,
    setViewMode,
    isLoading,
  } = useFilteredGalleryPagination({
    queryFn: trpc.trainingType.search.useQuery,
    queryInput: {
      searchTerm: debouncedSearchTerm,
    },
  });

  const createItem = trpc.trainingType.create.useMutation();
  const editItem = trpc.trainingType.edit.useMutation();
  const deleteItem = trpc.trainingType.delete.useMutation();

  const onError = useCallback(
    (error: { data?: { code?: string } | null }) => {
      if (error.data?.code === 'CONFLICT') {
        toast({ title: t('exerciseTypeAlreadyExists'), severity: 'error' });
      } else {
        toast({ title: t('somethingWentWrong'), severity: 'error' });
      }
    },
    [t, toast],
  );

  const onSave = useCallback(
    async (form: NameEntityForm, id?: string) => {
      if (id) {
        await editItem.mutateAsync(
          { id, name: form.name },
          {
            onSuccess: () => {
              toast({ title: t('exerciseTypeUpdated'), severity: 'success' });
              refetch();
            },
            onError,
          },
        );
      } else {
        await createItem.mutateAsync(
          { name: form.name },
          {
            onSuccess: () => {
              toast({ title: t('exerciseTypeCreated'), severity: 'success' });
              refetch();
            },
            onError,
          },
        );
      }
    },
    [createItem, editItem, t, refetch, toast, onError],
  );

  const onDelete = useCallback(
    async (id: string) => {
      await deleteItem.mutateAsync(
        { id },
        {
          onSuccess: () => {
            toast({ title: t('exerciseTypeDeleted'), severity: 'success' });
            refetch();
          },
          onError: () =>
            toast({ title: t('somethingWentWrong'), severity: 'error' }),
        },
      );
    },
    [deleteItem, t, refetch, toast],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: () => t('name'),
        cell: ({ row }) => row.original.name,
      }),
    ],
    [t],
  );

  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setDebouncedSearchTerm(value), 300),
    [],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      debouncedSetSearchTerm(value);
      setPage(0);
    },
    [debouncedSetSearchTerm, setPage],
  );

  const handleResetFilters = useCallback(() => {
    setSearchInput('');
    setDebouncedSearchTerm('');
    debouncedSetSearchTerm.clear();
    setPage(0);
  }, [debouncedSetSearchTerm, setPage]);

  return (
    <Gallery<NameEntity>
      title={t('exerciseType')}
      createButtonText={t('createExerciseType')}
      createDisabled={!enableCreate}
      createDisabledReason={t('permissionDisabled.create', {
        item: t('exerciseType'),
      })}
      data={data}
      columns={columns}
      viewMode={viewMode}
      isLoading={isLoading}
      setViewMode={setViewMode}
      page={page}
      pageSize={pageSize}
      setPage={setPage}
      setPageSize={setPageSize}
      pageSizeOptions={[25, 50, 100]}
      searchValue={searchInput}
      onSearchChange={handleSearchChange}
      onResetFilters={handleResetFilters}
      disableGridView={true}
      renderEditSheet={(selected, setSelected) => (
        <NameEntityDrawer
          entity={selected}
          open={!!selected}
          onClose={() => setSelected(null)}
          onSubmit={onSave}
          onDelete={onDelete}
          createTitle={t('createExerciseType')}
          editTitle={t('editExerciseType')}
          entityLabel={t('exerciseType')}
        />
      )}
      renderCreateSheet={
        enableCreate
          ? (isOpen, setIsOpen) => (
              <NameEntityDrawer
                open={isOpen}
                onClose={() => setIsOpen(false)}
                onSubmit={onSave}
                onDelete={onDelete}
                createTitle={t('createExerciseType')}
                editTitle={t('editExerciseType')}
                entityLabel={t('exerciseType')}
              />
            )
          : undefined
      }
    />
  );
}
